import firestore from "src/apiService/firestore";
import { getUserId } from "src/config/storage";
import type { BankAccount } from "src/interfaces/bank-account.interface";

/**
 * Find a bank account linked to the given GL account: first by
 * general_ledger_account_id, then by name. If none exists, creates one
 * and returns it.
 */
export async function getBankAccountForGlAccount(
  glAccountId: string,
  glAccountName: string
): Promise<BankAccount> {
  const db = firestore();
  const user = await getUserId();

  const byGlId = await db
    .collection("Bank_Accounts")
    .where("user", "==", user)
    .where("general_ledger_account_id", "==", glAccountId)
    .limit(1)
    .get();

  if (!byGlId.empty) {
    const doc = byGlId.docs[0];
    return { id: doc.id, ...doc.data() } as BankAccount;
  }

  const byName = await db
    .collection("Bank_Accounts")
    .where("user", "==", user)
    .where("name", "==", glAccountName)
    .limit(1)
    .get();

  if (!byName.empty) {
    const doc = byName.docs[0];
    return { id: doc.id, ...doc.data() } as BankAccount;
  }

  const bankAccountRef = db.collection("Bank_Accounts").doc();
  await bankAccountRef.set({
    id: bankAccountRef.id,
    name: glAccountName,
    user,
    general_ledger_account_id: glAccountId,
    type: "bank-account",
  });
  return {
    id: bankAccountRef.id,
    name: glAccountName,
    user,
    general_ledger_account_id: glAccountId,
    type: "bank-account",
  } as BankAccount;
}

export async function addBankAccount(
  account: Omit<BankAccount, "id" | "user">
) {
  const db = firestore();
  const user = await getUserId();
  const ref = db.collection("Bank_Accounts").doc();
  const value = {
    ...account,
    id: ref.id,
    user,
  };
  await ref.set(value);
  return value;
}

export async function updateBankAccountVisibility(
  id: string,
  { hidden }: { hidden: boolean }
) {
  const db = firestore();
  return await db.collection("Bank_Accounts").doc(id).update({ hidden });
}
